import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { practitionerProfilePath, PROMOTION_QUERY_PARAM } from "../../reviews/contract";

/**
 * The practitioners tab's "featured strip" (crm-api-contract.md's Promote bullet:
 * `GET /api/marketplace/featured?category` P, "counts views"; crm-frontend-design.md
 * §E.9's placement preview: "3-tile Featured strip").
 *
 * `GET /marketplace/featured` answers active PROFILE promotions ONLY
 * (`modules/crm/promotions.py:featured` — `item.type == "profile"`, most recently
 * started first): a practitioner-shaped row (`name, slug, site_url, logo_url, tagline`),
 * not a course/product one. A course/product promotion is buyable (Promote works for
 * all three item types) but this phase builds no marketplace surface for it yet — the
 * module's own docstring says so ("A course/product category-browse page is future
 * work, not this run's") — so this strip mounts on the PRACTITIONERS tab only.
 *
 * `category` scopes it to the currently selected specialty filter; unset shows every
 * active profile promotion platform-wide, most recent first.
 *
 * Decorative, not core: an empty or failed read renders nothing rather than an error
 * panel — the regular grid below it is the real content and must not be blocked by this.
 */
export default function FeaturedStrip({ category }) {
  const t = useT("marketplace");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/marketplace/featured", { params: category ? { category } : {} })
      .then(({ data }) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [category]);

  const track = (promotionId) => {
    if (!promotionId) return;
    api.post(`/marketplace/promotions/${promotionId}/click`, {}).catch(() => {});
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mb-8" data-testid="marketplace-featured-practitioners">
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sol-gold/80">
        <Sparkles className="w-3.5 h-3.5" /> {t("featured.title")}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.slice(0, 3).map((p) => (
          <Link
            key={p.promotion_id}
            // --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
            // `?pr=` rides this SAME-origin internal navigation; `<AffiliateRefCapture />`
            // (mounted once at the router root) stores it on arrival, and
            // `marketplaceLinks.withPromotion` reads it back onto the profile page's
            // own outbound "Book" links, which DO cross the origin.
            to={`${practitionerProfilePath(p.slug)}?${PROMOTION_QUERY_PARAM}=${encodeURIComponent(p.promotion_id)}`}
            // --- crm-module END ---
            onClick={() => track(p.promotion_id)}
            data-testid={`marketplace-featured-practitioner-${p.tenant_id}`}
            className="group flex items-center gap-3 rounded-2xl border border-sol-gold/30 bg-white/[0.04] p-4 hover:border-sol-gold/60 transition-all"
          >
            {p.logo_url ? (
              <img src={p.logo_url} alt="" className="h-12 w-12 rounded-full object-cover flex-shrink-0 border border-white/10" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-sol-gold/15 flex items-center justify-center text-sol-gold font-display flex-shrink-0">
                {(p.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-display text-sm text-white truncate group-hover:text-sol-gold transition">{p.name}</p>
              {p.tagline && <p className="text-xs text-white/50 line-clamp-1">{p.tagline}</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
